import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Boxes, ChevronRight, ExternalLink, FileText, Printer, RefreshCw, Search } from "lucide-react";
import QRCode from "qrcode";
import { Button, EmptyState, ErrorBanner, LoadingState, Modal, PageHeader, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { goodsReceipts, warehouseName } from "../lib/demoData";
import { requireSupabase } from "../lib/supabase";
import { OrderLink } from "../components/OrderLink";
import {
  formatDateTime,
  loadWarehouseSnapshot,
  receiptStatusPresentation,
  type DeliveryNoteSnapshot,
  type PackageSnapshot,
  type ReceiptSnapshot
} from "../lib/warehouseData";
import { useDeveloperSettings } from "../lib/developerSettings";

function escapeHtml(value: string) {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] ?? character);
}

function easyTecStatus(status: ReceiptSnapshot["easyTecStatus"]) {
  if (status === "uploaded") return { label: "Hochgeladen", tone: "success" as const };
  if (status === "not_required") return { label: "Nicht erforderlich", tone: "neutral" as const };
  return { label: "Upload offen", tone: "warning" as const };
}

function formatFileSize(value: number | null) {
  if (!value) return "–";
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / 1024 / 1024).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
}

const demoReceipts: ReceiptSnapshot[] = goodsReceipts.map((receipt) => ({
  id: receipt.id,
  workOrderId: receipt.id,
  orderNumber: receipt.orderNumber,
  orderDescription: null,
  supplier: receipt.supplier,
  department: receipt.department,
  warehouseId: "",
  warehouse: receipt.warehouse,
  warehouseName: warehouseName(receipt.warehouse),
  location: receipt.location,
  packageCount: receipt.packagesTotal,
  stockedPackages: receipt.packagesStored,
  pickedUpPackages: receipt.packagesTotal - receipt.packagesStored,
  returnedPackages: 0,
  receivedAt: receipt.receivedAt,
  status: receipt.packagesStored < receipt.packagesTotal ? "partially_picked_up" : "stored",
  returnStatus: null,
  returnRegisteredAt: null,
  returnPickedUpAt: null,
  deliveryNoteNumber: null,
  documents: [],
  documentId: null,
  documentStoragePath: null,
  documentMimeType: null,
  easyTecId: null,
  easyTecStatus: receipt.easyTecStatus === "hochgeladen" ? "uploaded" : "open"
}));

export function InventoryPage() {
  const { cloudConfigured } = useAuth();
  const { exampleTexts } = useDeveloperSettings();
  const [receipts, setReceipts] = useState<ReceiptSnapshot[]>([]);
  const [packages, setPackages] = useState<PackageSnapshot[]>([]);
  const [loading, setLoading] = useState(cloudConfigured);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptSnapshot | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const previewRequestId = useRef(0);

  const load = useCallback(async () => {
    if (!cloudConfigured) return;
    setLoading(true);
    try {
      const snapshot = await loadWarehouseSnapshot();
      setReceipts(snapshot.receipts.filter((receipt) => receipt.status !== "draft" && receipt.stockedPackages > 0));
      setPackages(snapshot.packages);
      setError("");
    } catch (loadError) {
      setError(`Lagerbestand konnte nicht geladen werden: ${loadError instanceof Error ? loadError.message : "Unbekannter Fehler"}`);
    } finally {
      setLoading(false);
    }
  }, [cloudConfigured]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const source = cloudConfigured ? receipts : demoReceipts;
    const term = search.trim().toLocaleLowerCase("de-DE");
    if (!term) return source;
    return source.filter((receipt) => [receipt.orderNumber, receipt.supplier, receipt.department, receipt.location, receipt.warehouseName, receipt.deliveryNoteNumber ?? ""]
      .some((value) => value.toLocaleLowerCase("de-DE").includes(term)));
  }, [cloudConfigured, receipts, search]);

  const selectedPackages = useMemo(() => selectedReceipt
    ? packages.filter((item) => item.receiptId === selectedReceipt.id)
    : [], [packages, selectedReceipt]);
  const selectedDocument = selectedReceipt?.documents.find((document) => document.id === selectedDocumentId) ?? null;

  const loadDocumentPreview = useCallback(async (document: DeliveryNoteSnapshot) => {
    const requestId = ++previewRequestId.current;
    setSelectedDocumentId(document.id);
    setPreviewUrl("");
    setPreviewError("");
    setPreviewLoading(true);
    try {
      const { data, error: signedUrlError } = await requireSupabase().storage
        .from("delivery-notes")
        .createSignedUrl(document.storagePath, 600);
      if (signedUrlError || !data?.signedUrl) throw signedUrlError ?? new Error("Kein Dokumentlink erhalten.");
      if (previewRequestId.current === requestId) setPreviewUrl(data.signedUrl);
    } catch (nextError) {
      if (previewRequestId.current === requestId) {
        setPreviewError(nextError instanceof Error ? nextError.message : "Lieferschein konnte nicht geöffnet werden.");
      }
    } finally {
      if (previewRequestId.current === requestId) setPreviewLoading(false);
    }
  }, []);

  function openDetails(receipt: ReceiptSnapshot) {
    setSelectedReceipt(receipt);
    setSelectedDocumentId(null);
    setPreviewUrl("");
    setPreviewError("");
    setPreviewLoading(false);
    if (receipt.documents[0]) void loadDocumentPreview(receipt.documents[0]);
  }

  const closeDetails = useCallback(() => {
    previewRequestId.current += 1;
    setSelectedReceipt(null);
    setSelectedDocumentId(null);
    setPreviewUrl("");
    setPreviewError("");
    setPreviewLoading(false);
  }, []);

  async function openLabels(receipt: ReceiptSnapshot) {
    const selected = packages.filter((item) => item.receiptId === receipt.id && item.status === "stocked");
    if (selected.length === 0) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      setError("Das Etikettenfenster wurde vom Browser blockiert. Pop-ups für diese Seite erlauben.");
      return;
    }
    printWindow.opener = null;
    printWindow.document.write("<p style='font-family:Arial;padding:24px'>Etiketten werden erzeugt …</p>");
    try {
      const labels = await Promise.all(selected.map(async (item) => ({
        item,
        qr: await QRCode.toDataURL(item.code, { width: 260, margin: 1, errorCorrectionLevel: "M" })
      })));
      printWindow.document.open();
      printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Etiketten ${escapeHtml(receipt.orderNumber)}</title><style>
        @page{size:102mm 76mm;margin:0}*{box-sizing:border-box}body{margin:0;font-family:Arial,sans-serif;color:#071c3e}.label{width:102mm;height:76mm;padding:6mm;display:grid;grid-template-columns:1fr 31mm;grid-template-rows:auto 1fr auto;gap:3mm;border:1px solid #d4dbe6;page-break-after:always}.brand{font-size:11pt;font-weight:800;color:#063f9b;letter-spacing:.05em}.order{margin-top:2mm;font-size:24pt;font-weight:900}.meta{display:grid;gap:1.5mm;align-content:start;font-size:11pt}.meta b{font-size:13pt}.qr{grid-column:2;grid-row:1/4;display:grid;align-content:center;text-align:center}.qr img{width:30mm;height:30mm}.qr strong{font-size:10pt;margin-top:2mm}.package{font-size:10pt;font-weight:700;color:#465772}@media screen{body{background:#eef2f7}.label{margin:8mm auto;background:#fff;box-shadow:0 2mm 8mm #0002}}@media print{.label{border:0}}
      </style></head><body>${labels.map(({ item, qr }) => `<section class="label"><div><div class="brand">K&amp;S LAGERSYSTEM · ${escapeHtml(item.warehouse)}</div><div class="order">${escapeHtml(item.orderNumber)}</div></div><div class="meta"><span><b>${escapeHtml(item.supplier)}</b></span><span>${escapeHtml(item.department)}</span><span>Lagerplatz <b>${escapeHtml(item.location)}</b></span></div><div class="package">Packstück ${item.packageNumber} von ${receipt.packageCount}</div><div class="qr"><img alt="QR ${escapeHtml(item.code)}" src="${qr}"><strong>${escapeHtml(item.code)}</strong></div></section>`).join("")}</body></html>`);
      printWindow.document.close();
      printWindow.focus();
      window.setTimeout(() => printWindow.print(), 250);
    } catch (labelError) {
      printWindow.close();
      setError(`Etiketten konnten nicht erzeugt werden: ${labelError instanceof Error ? labelError.message : "Unbekannter Fehler"}`);
    }
  }

  return (
    <>
      <PageHeader
        title="Lagerbestand"
        description={cloudConfigured ? "Alle aktuell eingelagerten und teilweise abgeholten Aufträge." : "Lokaler Vorschaumodus ohne Cloudverbindung."}
        action={cloudConfigured ? <Button variant="secondary" onClick={() => void load()} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button> : undefined}
      />
      {error && <ErrorBanner message={error} />}
      <div className="toolbar"><label className="search-box"><Search size={18} /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={exampleTexts.inventorySearch} /></label></div>
      {loading ? <LoadingState label="Lagerbestand wird geladen …" /> : filtered.length === 0 ? (
        <EmptyState title="Kein Lagerbestand gefunden" description={receipts.length === 0 && cloudConfigured ? "Bestätigte Wareneingänge erscheinen nach dem Lieferschein-Upload hier." : "Bitte Suchbegriff anpassen."} />
      ) : (
        <section className="inventory-grid">
          {filtered.map((receipt) => {
            const presentation = receiptStatusPresentation(receipt);
            return (
              <article
                className="inventory-card inventory-card--interactive"
                key={receipt.id}
                role="button"
                tabIndex={0}
                aria-label={`Details zu Auftrag ${receipt.orderNumber} öffnen`}
                onClick={() => openDetails(receipt)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
                  event.preventDefault();
                  openDetails(receipt);
                }}
              >
                <header><span><Boxes size={20} /></span><StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge></header>
                <strong><OrderLink orderNumber={receipt.orderNumber} stopPropagation /></strong>
                <p>{receipt.supplier} · {receipt.department}</p>
                <dl><div><dt>Lager</dt><dd>{receipt.warehouseName}</dd></div><div><dt>Regal</dt><dd>{receipt.location}</dd></div><div><dt>Vorhanden</dt><dd>{receipt.stockedPackages} von {receipt.packageCount}</dd></div></dl>
                <div className="inventory-card__actions">
                  <span className="inventory-card__detail-hint">Details <ChevronRight size={14} /></span>
                  {cloudConfigured && <Button variant="secondary" onClick={(event) => { event.stopPropagation(); void openLabels(receipt); }}><Printer size={15} /> Etiketten/QR</Button>}
                </div>
              </article>
            );
          })}
        </section>
      )}

      {selectedReceipt && (
        <Modal
          title={selectedReceipt.orderNumber}
          description={`${selectedReceipt.supplier} · ${selectedReceipt.department}`}
          onClose={closeDetails}
          size="large"
        >
          <div className="inventory-detail">
            <section className="inventory-detail__summary">
              <header>
                <div><span>Wareneingang</span><strong><OrderLink orderNumber={selectedReceipt.orderNumber} /></strong></div>
                <StatusBadge tone={receiptStatusPresentation(selectedReceipt).tone}>{receiptStatusPresentation(selectedReceipt).label}</StatusBadge>
              </header>
              <dl className="inventory-detail__facts">
                <div><dt>Lieferant</dt><dd>{selectedReceipt.supplier}</dd></div>
                <div><dt>Abteilung</dt><dd>{selectedReceipt.department}</dd></div>
                <div><dt>Lager</dt><dd>{selectedReceipt.warehouseName}</dd></div>
                <div><dt>Lagerplatz</dt><dd>{selectedReceipt.location}</dd></div>
                <div><dt>Wareneingang</dt><dd>{formatDateTime(selectedReceipt.receivedAt)}</dd></div>
                <div><dt>Lieferscheinnummer</dt><dd>{selectedReceipt.deliveryNoteNumber || "Nicht angegeben"}</dd></div>
                <div><dt>Packstücke vorhanden</dt><dd>{selectedReceipt.stockedPackages} von {selectedReceipt.packageCount}</dd></div>
                <div><dt>Bereits abgeholt</dt><dd>{selectedReceipt.pickedUpPackages}</dd></div>
                <div><dt>EasyTec</dt><dd><StatusBadge tone={easyTecStatus(selectedReceipt.easyTecStatus).tone}>{easyTecStatus(selectedReceipt.easyTecStatus).label}</StatusBadge></dd></div>
              </dl>
            </section>

            <section className="inventory-detail__packages">
              <h3>Packstücke</h3>
              {selectedPackages.length > 0 ? (
                <div className="package-chip-list">
                  {selectedPackages.map((item) => <span className={item.status === "stocked" ? "package-chip" : "package-chip package-chip--picked-up"} key={item.id}><strong>{item.code}</strong><small>Packstück {item.packageNumber} · {item.status === "stocked" ? "im Lager" : item.status === "returned" ? "retourniert" : "abgeholt"}</small></span>)}
                </div>
              ) : <p className="inventory-detail__empty">In der Vorschau sind keine Packstückcodes hinterlegt.</p>}
            </section>

            <section className="inventory-detail__documents">
              <header>
                <div><h3>Lieferschein</h3><p>Das Original wird über einen geschützten, zehn Minuten gültigen Link angezeigt.</p></div>
                {previewUrl && <Button variant="secondary" onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}><ExternalLink size={15} /> Separat öffnen</Button>}
              </header>
              {selectedReceipt.documents.length > 1 && (
                <div className="document-tabs" aria-label="Lieferscheine">
                  {selectedReceipt.documents.map((document, index) => (
                    <button className={document.id === selectedDocumentId ? "document-tab document-tab--active" : "document-tab"} type="button" key={document.id} onClick={() => void loadDocumentPreview(document)}>
                      <FileText size={15} /> Lieferschein {index + 1}
                    </button>
                  ))}
                </div>
              )}
              {selectedDocument && (
                <div className="document-meta">
                  <span><strong>{selectedDocument.originalFileName || "Lieferschein"}</strong><small>{selectedDocument.documentSource === "network_scanner" ? "Netzwerkscanner" : "Lagergerät"}</small></span>
                  <span><small>Erfasst</small><strong>{formatDateTime(selectedDocument.capturedAt)}</strong></span>
                  <span><small>Seiten</small><strong>{selectedDocument.pageCount ?? "–"}</strong></span>
                  <span><small>Dateigröße</small><strong>{formatFileSize(selectedDocument.sizeBytes)}</strong></span>
                </div>
              )}
              {previewError && <ErrorBanner message={`Lieferschein konnte nicht geladen werden: ${previewError}`} />}
              {previewLoading ? <LoadingState label="Lieferschein wird sicher geladen …" /> : previewUrl && selectedDocument ? (
                selectedDocument.mimeType === "application/pdf"
                  ? <iframe className="inventory-document-preview" title={`Lieferschein ${selectedReceipt.orderNumber}`} src={previewUrl} />
                  : <img className="inventory-document-image" src={previewUrl} alt={`Lieferschein ${selectedReceipt.orderNumber}`} />
              ) : !previewError && <EmptyState title="Kein Lieferschein hinterlegt" description={cloudConfigured ? "Zu diesem Wareneingang wurde noch kein Dokument verknüpft." : "Im Vorschaumodus ist kein echtes Dokument hinterlegt."} />}
            </section>
          </div>
        </Modal>
      )}
    </>
  );
}
